import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, Button, Box, TextField, IconButton,
  Card, CardContent, Grid, Tabs, Tab, Alert, CircularProgress,
  List, ListItem, Divider, Paper
} from '@mui/material';
import { Add, Delete, Save, Image, VideoLibrary, BusinessCenter, Palette, Link as LinkIcon } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const AdminHomepageCustomization = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settings, setSettings] = useState({
    carousel: [{ url: '', title: '' }],
    video_url: '',
    hero_image: '',
    navbar_logo: '/usc-logo.png',
    action_cards: [
      { title: '', description: '', icon: 'school', link: '' },
      { title: '', description: '', icon: 'business', link: '' },
      { title: '', description: '', icon: 'info', link: '' }
    ]
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/homepage/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('idToken');
      await axios.post(
        `${API_BASE_URL}/homepage/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Homepage settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Carousel Management
  const addCarouselImage = () => {
    setSettings({
      ...settings,
      carousel: [...settings.carousel, { url: '', title: '' }]
    });
  };

  const updateCarouselImage = (index, field, value) => {
    const newCarousel = [...settings.carousel];
    newCarousel[index][field] = value;
    setSettings({ ...settings, carousel: newCarousel });
  };

  const removeCarouselImage = (index) => {
    if (settings.carousel.length > 1) {
      const newCarousel = settings.carousel.filter((_, i) => i !== index);
      setSettings({ ...settings, carousel: newCarousel });
    }
  };

  // Action Cards Management
  const updateActionCard = (index, field, value) => {
    const newCards = [...settings.action_cards];
    newCards[index][field] = value;
    setSettings({ ...settings, action_cards: newCards });
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading homepage settings...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          🎨 Homepage Customization
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#046A38', '&:hover': { bgcolor: '#035028' } }}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
          <Tab label="📸 Carousel" />
          <Tab label="🎬 Video & Hero" />
          <Tab label="🎯 Action Cards" />
        </Tabs>
      </Paper>

      {/* Tab 1: Carousel Images */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Alumni Memories Carousel</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={addCarouselImage}>
                Add Image
              </Button>
            </Box>

            <List>
              {settings.carousel.map((item, index) => (
                <Box key={index}>
                  <ListItem sx={{ px: 0 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={`Image ${index + 1} URL`}
                          value={item.url}
                          onChange={(e) => updateCarouselImage(index, 'url', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Caption (optional)"
                          value={item.title}
                          onChange={(e) => updateCarouselImage(index, 'title', e.target.value)}
                          placeholder="Graduation Day 2024"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          onClick={() => removeCarouselImage(index)}
                          color="error"
                          disabled={settings.carousel.length === 1}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                      {item.url && (
                        <Grid item xs={12}>
                          <Box
                            component="img"
                            src={item.url}
                            alt={item.title || 'Preview'}
                            sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                  {index < settings.carousel.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              💡 Use high-quality images (1200x800px recommended). Images will display in carousel on homepage.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Video & Hero */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <VideoLibrary sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Welcome Video
                </Typography>
                <TextField
                  fullWidth
                  label="YouTube Embed URL"
                  value={settings.video_url}
                  onChange={(e) => setSettings({ ...settings, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/embed/VIDEO_ID"
                  helperText="Get embed URL: YouTube video → Share → Embed → Copy URL from iframe src"
                />
                {settings.video_url && (
                  <Box sx={{ mt: 2, position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                      src={settings.video_url}
                      title="Video Preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: 8
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Image sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Hero Background Image
                </Typography>
                <TextField
                  fullWidth
                  label="Hero Image URL"
                  value={settings.hero_image}
                  onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
                  placeholder="https://example.com/hero.jpg (optional)"
                  helperText="Large background image for action cards section (optional)"
                />
                {settings.hero_image && (
                  <Box
                    component="img"
                    src={settings.hero_image}
                    alt="Hero preview"
                    sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 1, mt: 2 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 3: Action Cards */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              <BusinessCenter sx={{ verticalAlign: 'middle', mr: 1 }} />
              Action Cards (Hero Section)
            </Typography>

            <Grid container spacing={3}>
              {settings.action_cards.map((card, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Card {index + 1}
                      </Typography>
                      <TextField
                        fullWidth
                        label="Title"
                        value={card.title}
                        onChange={(e) => updateActionCard(index, 'title', e.target.value)}
                        placeholder="Alumni Registration"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={card.description}
                        onChange={(e) => updateActionCard(index, 'description', e.target.value)}
                        placeholder="Join our alumni database..."
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Link/Action"
                        value={card.link || ''}
                        onChange={(e) => updateActionCard(index, 'link', e.target.value)}
                        placeholder="/register/alumni OR https://usc.edu.tt/give OR mailto:info@usc.edu.tt?subject=Alumni"
                        helperText="Internal path, external URL, or mailto link"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Material Icon Name"
                        value={card.icon}
                        onChange={(e) => updateActionCard(index, 'icon', e.target.value)}
                        placeholder="school"
                        helperText="school, business, info, people, work, favorite, etc."
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <strong>💡 Link Examples:</strong><br/>
              • Internal: <code>/register/alumni</code> or <code>/contact</code><br/>
              • External: <code>https://usc.edu.tt/give</code><br/>
              • Email: <code>mailto:info@usc.edu.tt?subject=Alumni Network Inquiry</code><br/>
              • Icons: Browse at <a href="https://mui.com/material-ui/material-icons/" target="_blank" rel="noopener noreferrer">MUI Icons Gallery</a>
            </Alert>
          </CardContent>
        </Card>
      )}

    </Container>
  );
};

export default AdminHomepageCustomization;